import { useEffect, useState } from "react";

/** Below this, a viewport change is an address bar, not a keyboard. */
const KEYBOARD_THRESHOLD = 120;

/** Long enough to cover the keyboard sliding in or out. */
const TRANSITION_MS = 800;

/**
 * iPhone/iPad, in any browser — they are all WebKit. iPadOS reports itself as a
 * Mac, so a touch-capable "Mac" counts too. Same test as index.html.
 */
const IS_IOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const isEditable = (el: Element | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/**
 * Calls `onChange` whenever the visual viewport may have moved or resized.
 *
 * iOS reports the keyboard's viewport late — often only once the slide has
 * finished — so around focus changes the viewport is also read every frame
 * for the length of the animation. That is what keeps things moving with the
 * keyboard rather than jumping after it.
 */
function watchVisualViewport(
  vv: VisualViewport,
  onChange: () => void,
): () => void {
  let raf = 0;
  let until = 0;
  const track = () => {
    onChange();
    raf = performance.now() < until ? requestAnimationFrame(track) : 0;
  };
  const onFocusChange = () => {
    until = performance.now() + TRANSITION_MS;
    if (!raf) raf = requestAnimationFrame(track);
  };

  onChange();
  vv.addEventListener("resize", onChange);
  vv.addEventListener("scroll", onChange);
  window.addEventListener("orientationchange", onChange);
  document.addEventListener("focusin", onFocusChange);
  document.addEventListener("focusout", onFocusChange);

  return () => {
    cancelAnimationFrame(raf);
    vv.removeEventListener("resize", onChange);
    vv.removeEventListener("scroll", onChange);
    window.removeEventListener("orientationchange", onChange);
    document.removeEventListener("focusin", onFocusChange);
    document.removeEventListener("focusout", onFocusChange);
  };
}

/**
 * On iOS, pins a `position: fixed` element over exactly the part of the page
 * that is on screen.
 *
 * iOS (Safari and Chrome alike — both are WebKit) does not resize the page for
 * its keyboard or its toolbars the way Android does. It slides the visual
 * viewport over the page instead, and can leave it slid after the keyboard has
 * gone — `window.scrollTo` cannot undo that. Anything laid out against the page
 * then shows up shifted: header cut off at the top, a band of nothing above the
 * bottom bar. `visualViewport.offsetTop` and `.height` say where the screen
 * actually is, so the app shell is placed there directly.
 *
 * iOS only. Everywhere else the element keeps its CSS box (`100dvh` from the
 * top), which is already right: Android resizes the page for the keyboard
 * itself (`interactive-widget=resizes-content`), and its visual viewport can
 * reach under the gesture bar — pinning to it there pushed the bottom of the
 * screen under the tab bar.
 *
 * Takes the element itself (from a callback ref) so it attaches whenever the
 * shell mounts.
 */
export function usePinToVisualViewport(el: HTMLElement | null): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!IS_IOS || !el || !vv) return;

    const apply = () => {
      // Pinch-zoom moves the visual viewport too; leave the app where it is.
      if (vv.scale > 1.01) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
    };

    const stop = watchVisualViewport(vv, apply);
    return () => {
      stop();
      el.style.top = "";
      el.style.height = "";
    };
  }, [el]);
}

/**
 * Whether a software keyboard is up.
 *
 * Measured against the tallest visual viewport seen at this width rather than
 * against `innerHeight`, which some iOS versions shrink along with the
 * keyboard — comparing the two then reads 0. The visual viewport shrinks
 * either way. False on desktop, so callers can use it blindly.
 */
export function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    // Tallest unobstructed height at the current width; reset on rotation.
    let full = vv.height;
    let width = window.innerWidth;

    return watchVisualViewport(vv, () => {
      if (vv.scale > 1.01) return;
      if (window.innerWidth !== width) {
        width = window.innerWidth;
        full = vv.height;
      }
      full = Math.max(full, vv.height);
      setOpen(
        isEditable(document.activeElement) &&
          full - vv.height > KEYBOARD_THRESHOLD,
      );
    });
  }, []);

  return open;
}
